import axios from "axios";

export const GET_USER_PROFILE_URL = `/v2/user/profile/`;
export const GET_USER_ROLES_URL = `/v2/role/assign`;
export const ADD_USER_URL = `/v2/user/create/`;
export const GET_CATEGORY_LIST_URL = "/v2/category";
export const GET_USER_ROLE_PERMISSION_URL = "/v2/role/permission";
export const GET_USER_DATA = "/v2/user/data";
export const GET_RECENT_ACTIVITIES_URL = "/v2/activities";

export function getUserProfile(userId) {
    return axios.get(`${GET_USER_PROFILE_URL}${userId}`);
}
export function getUserData(userId) {
    return axios.get(`${GET_USER_DATA}/${userId}`);
}

export function getUserRoles() {
    return axios.get(GET_USER_ROLES_URL);
}

export function addUser(value) {
    return axios.post(`${ADD_USER_URL}${value && value.userId}`, { ...value });
}

export function getCategoryList() {
    return axios.get(GET_CATEGORY_LIST_URL);
}

export function archiveCategory(id) {
    return axios.post(`/v2/category/archive/${id}`);
}

export function unarchiveCategory(id) {
    return axios.post(`/v2/category/unarchive/${id}`);
}

export function getUserRolesCreated(userId) {
    return axios.get(`/v2/role/createdBy`);
}

export function getUserRolePermission(id) {
    return axios.get(`${GET_USER_ROLE_PERMISSION_URL}/${id}`);
}

export function updateUserRolePermission(value) {
    return axios.post(`${GET_USER_ROLE_PERMISSION_URL}/${value.id}`, value);
}

export function getUserRecentActivities(userId) {
    return axios.get(`${GET_RECENT_ACTIVITIES_URL}/${userId}`);
}

export function getListOfUserAssignableToAsset() {
    return axios.get(`/v2/asset/assign`);
}

export function getListOfUserCreatedCluster() {
    // Every site, but only what a dropdown needs (id, name, cluster ID, location).
    return axios.get(`/v2/cluster/createdBy`, { params: { lite: true } });
}

export function getClusterPage(params) {
    return axios.get(`/v2/cluster/createdBy`, { params });
}

export function getAssetPage(params) {
    return axios.get(`/v2/asset/created`, { params });
}

// A page of assets (id, name, Asset ID) for pickers that search as you type.
export function getAssetOptions(params) {
    return axios.get(`/v2/asset/options`, { params });
}

export function createAsset(value) {
    return axios.post(`/v2/asset/create/`, value);
}

export function createCluster(value) {
    return axios.post(`/v2/cluster/create/`, value);
}

export function getListOfAssetCreatedBy() {
    return axios.get(`/v2/asset/created`);
}

export function getAssetAssignees(assetId) {
    return axios.get(`/v2/asset/assignees/${assetId}`);
}

export function addAssetAssignee(assetId, userId, role) {
    return axios.post(`/v2/asset/assignees/${assetId}`, { userId, role });
}

export function removeAssetAssignee(assetId, userId) {
    return axios.delete(`/v2/asset/assignees/${assetId}/${userId}`);
}

export function getClusterAssignees(clusterId) {
    return axios.get(`/v2/cluster/assignees/${clusterId}`);
}

export function addClusterAssignee(clusterId, userId, role) {
    return axios.post(`/v2/cluster/assignees/${clusterId}`, { userId, role });
}

export function removeClusterAssignee(clusterId, userId) {
    return axios.delete(`/v2/cluster/assignees/${clusterId}/${userId}`);
}

// Paginated + searchable list of a site's own assets - used by
// SiteAssetList.js so a Site Detail page with thousands of assets can be
// browsed without fetching/rendering them all at once.
export function getClusterAssets(clusterId, params) {
    return axios.get(`/v2/cluster/${clusterId}/assets`, { params });
}

// All observations submitted against a given Service Request (any
// submitter, scoped to the SR owner) - used to show them inline on the SR
// detail page instead of requiring a separate navigation.
export function getObservationsForSr(srId) {
    return axios.get(`/v2/observation/report/${srId}`);
}

// Manage which external Service Providers (MRO), Technicians, and Suppliers
// are linked to the logged-in asset owner's roster - see repo notes on the
// cross-tenant provider-visibility fix. Only linked providers show up in
// assignment dropdowns (Responsible Party, Send SR/WO, PO Supplier, etc).
export function getLinkedProviders() {
    return axios.get(`/v2/user/linked-providers`);
}

export function linkProvider(email) {
    return axios.post(`/v2/user/link-provider`, { email });
}

export function unlinkProvider(userId) {
    return axios.delete(`/v2/user/unlink-provider/${userId}`);
}

export function createSLA(value) {
    return axios.post(`/v2/sla/create/`, value);
}

export function getSla(id) {
    return axios.get(`/v2/sla/${id}`);
}

export function editSLA(value) {
    return axios.put(`/v2/sla/update/${value._id}`, value);
}

export function closeSR(id) {
    return axios.get(`/v2/sr/close/${id}`);
}

export function markSrAsRaised(id) {
    return axios.get(`/v2/sr/raised/${id}`);
}

export function acceptSR(value) {
    return axios.post(`/v2/sr/sent/accept/${value._id}`, { ...value });
}

export function assignSR(value) {
    return axios.post(`/v2/sr/assign/${value._id}`, { ...value });
}

export function getAssignList() {
    return axios.get(`/v2/user/assign/list`);
}

export function closeWO(id) {
    return axios.get(`/v2/wo/close/${id}`);
}

export function approveWO(id) {
    return axios.post(`/v2/wo/approve/${id}`, { approve: true });
}

export function acceptWO(value) {
    return axios.post(`/v2/wo/sent/accept/${value._id}`, { ...value });
}

export function assignWO(value) {
    return axios.post(`/v2/wo/assign/${value._id}`, { ...value });
}

export function completeWO(value) {
    return axios.post(`/v2/wo/complete/${value._id}`);
}

export function certifyWO(value) {
    return axios.post(`/v2/wo/certify/${value._id}`, { ...value });
}

export function acceptObservation(value) {
    return axios.post(`/v2/observation/accept/${value._id}`, { ...value });
}

export function rejectObservation(value) {
    return axios.post(`/v2/observation/accept/${value._id}`, { ...value });
}

export function rateUser(value) {
    return axios.post(`/v2/user/rate-user/${value._id}`, { ...value });
}

export function closePO(id) {
    return axios.get(`/v2/purchase-order/close/${id}`);
}

export function acceptPO(value) {
    return axios.post(`/v2/purchase-order/sent/accept/${value._id}`, {
        ...value
    });
}

export function acceptMR(value) {
    return axios.post(`/v2/material-request/accept/${value.id}`, { ...value });
}

// Asset Coverage Management (Warranty/Licence/Certification/Insurance/
// Contract) - one generic set of CRUD helpers for every coverage type.
export function getCoverageTypes() {
    return axios.get(`/v2/asset-coverage/types`);
}

export function getAssetCoverage(assetId) {
    return axios.get(`/v2/asset-coverage/asset/${assetId}`);
}

export function getAssetCoverageDetail(id) {
    return axios.get(`/v2/asset-coverage/${id}`);
}

export function createAssetCoverage(value) {
    return axios.post(`/v2/asset-coverage/create`, value);
}

export function editAssetCoverage(id, value) {
    return axios.put(`/v2/asset-coverage/edit/${id}`, value);
}

export function cancelAssetCoverage(id) {
    return axios.delete(`/v2/asset-coverage/${id}`);
}

// Asset Maintenance Planning - CRUD for the recurring plan TEMPLATE.
// Actual generated work is a real Service Request/Work Order, reachable
// via the existing SR/WO detail pages (no separate maintenance execution
// UI is needed).
export function getMaintenancePlans(assetId) {
    return axios.get(`/v2/maintenance-plan/asset/${assetId}`);
}

export function getMaintenancePlanDetail(id) {
    return axios.get(`/v2/maintenance-plan/${id}`);
}

export function createMaintenancePlan(value) {
    return axios.post(`/v2/maintenance-plan/create`, value);
}

export function editMaintenancePlan(id, value) {
    return axios.put(`/v2/maintenance-plan/edit/${id}`, value);
}

export function activateMaintenancePlan(id) {
    return axios.post(`/v2/maintenance-plan/${id}/activate`);
}

export function deactivateMaintenancePlan(id) {
    return axios.post(`/v2/maintenance-plan/${id}/deactivate`);
}

// QR-based Asset Identification
export function scanAsset(qrToken) {
    return axios.get(`/v2/asset/scan/${qrToken}`);
}

// Asset Depreciation & Valuation - operational (not accounting/statutory)
// asset book-value tracking. Every existing profile/period is read-only
// history - a new profile version is always CREATED (never edited/
// deleted), see src/controller/assetValuation.js.
export function getDepreciationMethods() {
    return axios.get(`/v2/asset-valuation/types`);
}

export function getAssetValuationProfiles(assetId) {
    return axios.get(`/v2/asset-valuation/profile/${assetId}`);
}

export function createAssetValuationProfile(assetId, value) {
    return axios.post(`/v2/asset-valuation/profile/${assetId}`, value);
}

export function getAssetBookValue(assetId) {
    return axios.get(`/v2/asset-valuation/book-value/${assetId}`);
}

export function getAssetValuationHistory(assetId) {
    return axios.get(`/v2/asset-valuation/history/${assetId}`);
}


export function regenerateAssetQrToken(id) {
    return axios.post(`/v2/asset/${id}/regenerate-qr-token`);
}

export function generateAssetQrToken(id) {
    return axios.post(`/v2/asset/${id}/generate-qr-token`);
}

export function getQrConfig() {
    return axios.get(`/v2/asset/qr-config`);
}

export function getQrLabelAssets() {
    return axios.get(`/v2/asset/qr-labels`);
}

// Public fault portal: asset selection by QR / Asset ID (no login). Both return only the
// minimal public asset context and are scoped to the portal page's owner.
export function publicFaultScan(qrToken, ownerId) {
    return axios.get(`/v2/asset/public-fault/scan/${encodeURIComponent(qrToken)}`, { params: { owner: ownerId } });
}

export function publicFaultLookup(assetID, ownerId) {
    return axios.get(`/v2/asset/public-fault/lookup/${encodeURIComponent(assetID)}`, { params: { owner: ownerId } });
}

export function getPublicFaultQrConfig() {
    return axios.get(`/v2/asset/public-fault/config`);
}

// Role-aware Maintenance Asset View (allowlisted fields, permission-filtered history).
export function getMaintenanceAssetView(assetId) {
    return axios.get(`/v2/maintenance-view/asset/${assetId}`);
}

// Material Request lifecycle (the Work Order is the parent record).
export function getWoMaterialRequests(woId) {
    return axios.get(`/v2/material-request/${woId}`);
}

export function materialRequestAction(action, id, body = {}) {
    return axios.post(`/v2/material-request/${action}/${id}`, body);
}

// One page of material requests: view = incoming (on my organisation's work orders) | mine (raised by me).
export function getMaterialRequestPage(params) {
    return axios.get("/v2/material-request/list", { params });
}

export function findAssetByAssetId(assetID) {
    return axios.get(`/v2/asset/lookup/by-asset-id/${encodeURIComponent(assetID)}`);
}

// Physical Asset Verification Campaigns
export function getVerificationCampaigns() {
    return axios.get(`/v2/verification-campaign`);
}

export function getVerificationCampaignDetail(id) {
    return axios.get(`/v2/verification-campaign/${id}`);
}

export function createVerificationCampaign(value) {
    return axios.post(`/v2/verification-campaign/create`, value);
}

export function editVerificationCampaign(id, value) {
    return axios.put(`/v2/verification-campaign/edit/${id}`, value);
}

export function deleteVerificationCampaign(id) {
    return axios.delete(`/v2/verification-campaign/${id}`);
}

export function previewVerificationCampaignScope(id) {
    return axios.get(`/v2/verification-campaign/${id}/preview-scope`);
}

export function activateVerificationCampaign(id) {
    return axios.post(`/v2/verification-campaign/${id}/activate`);
}

export function completeVerificationCampaign(id) {
    return axios.post(`/v2/verification-campaign/${id}/complete`);
}

export function closeVerificationCampaign(id) {
    return axios.post(`/v2/verification-campaign/${id}/close`);
}

export function getVerificationTasks(campaignId, params) {
    return axios.get(`/v2/verification-campaign/${campaignId}/tasks`, { params });
}

export function getVerificationExceptions(campaignId) {
    return axios.get(`/v2/verification-campaign/${campaignId}/exceptions`);
}

export function submitVerificationResult(taskId, value) {
    return axios.post(`/v2/verification-campaign/task/${taskId}/verify`, value);
}

export function claimVerificationTask(taskId) {
    return axios.post(`/v2/verification-campaign/task/${taskId}/claim`);
}

export function assignVerificationTask(taskId, value) {
    return axios.post(`/v2/verification-campaign/task/${taskId}/assign`, value);
}

export function bulkAssignVerificationTasks(value) {
    return axios.post(`/v2/verification-campaign/task/bulk-assign`, value);
}

export function getVerificationEligibleUsers(campaignId) {
    return axios.get(`/v2/verification-campaign/${campaignId}/eligible-users`);
}

export function getVerificationAssignmentSummary(campaignId) {
    return axios.get(`/v2/verification-campaign/${campaignId}/assignment-summary`);
}

// Pending verification tasks the caller can act on for one (authorised) asset
// - the verification side of the QR / Asset ID flow.
export function getAssetVerificationTasks(assetId) {
    return axios.get(`/v2/verification-campaign/asset/${assetId}/tasks`);
}

// "What work do I have on this asset?" - the My Work side of the QR flow.
export function getMyWorkForAsset(assetId) {
    return axios.get(`/v2/my-work/asset/${assetId}`);
}

export function scanAssetForCampaign(campaignId, qrToken) {
    return axios.get(`/v2/verification-campaign/${campaignId}/scan/${qrToken}`);
}

export function rejectMR(value) {
    return axios.post(`/v2/material-request/reject/${value.id}`, { ...value });
}

export function approvalList() {
    return axios.get(`/v2/approval/wo`);
}
export function approvalListPo() {
    return axios.get(`/v2/approval/po`);
}

export function createApproval(value) {
    return axios.post(`/v2/approval/create/wo`, { ...value });
}
export function createApprovalPO(value) {
    return axios.post(`/v2/approval/create/po`, { ...value });
}

export function editApproval(value) {
    return axios.post(`/v2/approval/update/${value._id}`, { ...value });
}

export function deleteApproval(value) {
    return axios.post(`/v2/approval/delete/${value}`);
}

export function getApproval() {
    return axios.get(`/v2/wo/sent/approval`);
}

export function getServiceProvider() {
    return axios.get(`/v2/user/service-provider`);
}

export function getAllScoreCard() {
    return axios.get(`/v2/score-card/all/list`);
}

export function getScore(value) {
    return axios.get(`/v2/score-card/${value}`);
}

export function getAssignedAsset() {
    return axios.get(`/v2/asset/assigned`);
}

export function getAssignedAssetDetails(value) {
    return axios.get(`/v2/asset/assigned-asset/details/${value}`);
}

export function getAssignedClusterDetails(value) {
    return axios.get(`/v2/asset/assigned-cluster/details/${value}`);
}

// Phase 1 - Asset Audit & Data Reconciliation. `type` is 'asset' or 'cluster';
// both back-ends expose the identical `/audit/*` sub-routes so a single
// shared helper set covers both tabs of the Audit page.
export function getAuditList(type, params) {
    return axios.get(`/v2/${type}/audit/list`, { params });
}

export function getAuditDuplicates(type) {
    return axios.get(`/v2/${type}/audit/duplicates`);
}

export function setAuditStatus(type, id, status, note) {
    return axios.post(`/v2/${type}/audit/status/${id}`, { status, note });
}

export function bulkSetAuditStatus(type, ids, status, note) {
    return axios.post(`/v2/${type}/audit/bulk-status`, { ids, status, note });
}

export function bulkUpdateAuditData(type, ids, fields) {
    return axios.post(`/v2/${type}/audit/bulk-update`, { ids, ...fields });
}

// Pending top-tenant registration approval (Super Admin only)
export function getRegistrationRequests() {
    return axios.get(`/v2/user/register/requests`);
}

export function getCompanyDetails(id) {
    return axios.get(`/v2/company/${id}`);
}

export function getCompanyMembers(id, params) {
    return axios.get(`/v2/company/${id}/members`, { params });
}

export function approveRegistration(userId, roleType) {
    return axios.post(`/v2/user/approve-registration/${userId}`, { roleType });
}

export function getRegistrationRoleOptions() {
    return axios.get(`/v2/user/register/role-options`);
}

export function requestRegistrationChanges(userId, reason, step) {
    return axios.post(`/v2/user/registration/request-changes/${userId}`, { reason, step });
}

export function rejectRegistration(userId, reason) {
    return axios.post(`/v2/user/reject-registration/${userId}`, { reason });
}

export function suspendAccount(userId, reason) {
    return axios.post(`/v2/user/registration/suspend/${userId}`, { reason });
}

export function unsuspendAccount(userId) {
    return axios.post(`/v2/user/registration/unsuspend/${userId}`);
}

// Onboarding (Batch 2/3) - Welcome path selection, Individual/Organisation
// onboarding forms, verification documents, submit-for-review.
export function getOnboardingStatus() {
    return axios.get(`/v2/user/onboarding/status`);
}

export function selectOnboardingPath(onboardingType) {
    return axios.post(`/v2/user/onboarding/select-path`, { onboardingType });
}

export function saveIndividualOnboarding(value) {
    return axios.put(`/v2/user/onboarding/individual`, value);
}

export function saveOrganisationOnboarding(value) {
    return axios.put(`/v2/user/onboarding/organisation`, value);
}

export function saveOnboardingContact(value) {
    return axios.put(`/v2/user/onboarding/contact`, value);
}

export function addOnboardingDocument(value) {
    return axios.post(`/v2/user/onboarding/documents`, value);
}

export function removeOnboardingDocument(docId) {
    return axios.delete(`/v2/user/onboarding/documents/${docId}`);
}

export function submitOnboarding() {
    return axios.post(`/v2/user/onboarding/submit`);
}

export function getDocumentRequirements() {
    return axios.get(`/v2/company/document-requirements`);
}

export function getMyCompany() {
    return axios.get(`/v2/company/mine`);
}

// Organisation Admin area - all scoped server-side to the caller's own
// company (see src/controller/organisation.js), never a client-supplied id.
export function updateMyOrganisation(value) {
    return axios.put(`/v2/organisation/profile`, value);
}

export function getOrganisationUsers() {
    return axios.get(`/v2/organisation/users`);
}

export function resendInvite(inviteId) {
    return axios.post(`/v2/organisation/invites/${inviteId}/resend`);
}

// Self-service "join an existing organisation" invite flow.
export function inviteUserToCompany(email, roleId) {
    return axios.post(`/v2/user/invite`, { email, roleId });
}

export function getSentInvites() {
    return axios.get(`/v2/user/invites`);
}

export function revokeInvite(inviteId) {
    return axios.post(`/v2/user/invite/${inviteId}/revoke`);
}

export function getInviteByToken(token) {
    return axios.get(`/v2/user/invite/${token}`);
}

export function acceptInvite(token, value) {
    return axios.post(`/v2/user/accept-invite/${token}`, value);
}

// Phase 4 - Unified Asset History: combined Fault/SR/WO/Observation/
// Material Request timeline + counts for a single asset. `page` is
// optional (defaults to page 1 server-side, 100 items per page).
export function getAssetHistory(assetId, page) {
    return axios.get(`/v2/asset/history/${assetId}`, { params: page ? { page } : {} });
}
// Phase 4 follow-up - same combined timeline, scoped to a Cluster/site
// (includes events for every asset located within it too).
export function getClusterHistory(clusterId, page) {
    return axios.get(`/v2/cluster/history/${clusterId}`, { params: page ? { page } : {} });
}
// StockMovement ledger - per-material inventory movement history.
export function getMaterialMovements(materialId) {
    return axios.get(`/v2/warehouse/material/${materialId}/movements`);
}
// Standardised Audit/Event trail (src/controller/auditEvent.js) - tenant-scoped
// server-side, never a client-supplied organisation id.
export function getAuditEvents(params) {
    return axios.get(`/v2/audit-events`, { params: params || {} });
}
export function getEntityAuditEvents(entityType, entityId, page) {
    return axios.get(`/v2/audit-events/entity/${entityType}/${entityId}`, { params: page ? { page } : {} });
}
// "My Work" - role-aware aggregation of the current user's own actionable
// items (src/controller/myWork.js) - read-only, never a replacement for
// any module's own list endpoints.
export function getMyWork() {
    return axios.get(`/v2/my-work`);
}
export function approvePO(id) {
    return axios.post(`/v2/purchase-order/approve/${id}`, { approve: true });
}
export function editUser(value) {
    return axios.put(`/v2/user/edit/${value._id}`, { ...value });
}

export function assgnAssetToTech(value) {
    return axios.post(`/v2/asset/technician/asset/assign/${value._id}`, {
        ...value
    });
}
export function assgnclusterToTech(value) {
    return axios.post(`/v2/asset/technician/cluster/assign/${value._id}`, {
        ...value
    });
}

export function getAssgnAssetToTech() {
    return axios.get(`/v2/asset/technician/assigned`);
}

export function createSrConfig(value) {
    return axios.post(`/v2/configuration/create`, { ...value });
}

export function updateSrConfig(value) {
    return axios.put(`/v2/configuration/update/${value.id}`, { ...value });
}


export function getSrConfig(value) {
    return axios.get(`/v2/configuration/created/sr`, { ...value });
}

export function getWoConfig(value) {
    return axios.get(`/v2/configuration/created/wo`, { ...value });
}

// Per-organisation "who gets notified about shared-pool verification
// tasks becoming available" setting - reuses the SAME generic
// create/update endpoints as SR/WO config above (createSrConfig/
// updateSrConfig already just take a plain {user, type} payload).
export function getVerificationNotificationConfig() {
    return axios.get(`/v2/configuration/created/verification`);
}
// The signed-in user's own pending invitations (works with no active organisation), and answering them.
export function getMyInvitations() {
    return axios.get(`/v2/user/invitations`);
}

export function answerMyInvitation(inviteId, accept) {
    return axios.post(`/v2/user/invitations/${inviteId}/${accept ? "accept" : "reject"}`);
}

// Decline by emailed link (no account or sign-in needed)
export function rejectInviteByToken(token) {
    return axios.post(`/v2/user/invite/${token}/reject`);
}
